/*
 * AutopilotStrategyCard
 * ---------------------
 * The Autopilot Strategy & Personalization surface. Lets the owner
 * tell Postmoro exactly what they want Autopilot to prioritize BEFORE
 * (or after) it starts filling their calendar. Every field maps
 * 1:1 onto the corresponding `strategy_*` column on the autopilot cfg.
 *
 * Precedence at read-time (backend side): Strategy > Brand Learning >
 * Business Profile. This component does NOT overwrite those systems —
 * it only exposes the strategy override layer.
 *
 * Grouped into progressive-disclosure cards so the owner isn't
 * overwhelmed. Every save calls the existing PUT /autopilot/settings
 * endpoint (partial update; other cfg fields untouched).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Target, Package, Users, Sparkles, ShieldOff, TrendingUp, Palette, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import GlassCard from "@/components/GlassCard";
import api from "@/lib/api";

const GOAL_OPTIONS = [
  { key: "leads", label: "Generate leads" },
  { key: "sales", label: "Drive sales" },
  { key: "brand_awareness", label: "Build brand awareness" },
  { key: "engagement", label: "Increase engagement" },
  { key: "traffic", label: "Drive website traffic" },
  { key: "bookings", label: "Get bookings / appointments" },
  { key: "promote", label: "Promote products/services" },
  { key: "local_awareness", label: "Grow local awareness" },
  { key: "education", label: "Educate customers" },
];

const TOPIC_PRIORITY = [
  "educational_tips", "product_benefits", "customer_pain_points",
  "behind_the_scenes", "case_studies", "local_community",
  "tutorials", "industry_insights",
];

const CONTENT_TYPES = [
  "educational", "promotional", "product_spotlight", "brand_story",
  "engagement", "authority", "behind_the_scenes", "local", "seasonal", "testimonial",
];

const TONES = [
  { key: "match_brand", label: "Match my brand" },
  { key: "professional", label: "Professional" },
  { key: "friendly", label: "Friendly" },
  { key: "bold", label: "Bold" },
  { key: "conversational", label: "Conversational" },
  { key: "premium", label: "Premium" },
  { key: "playful", label: "Playful" },
  { key: "educational", label: "Educational" },
  { key: "warm", label: "Warm" },
  { key: "direct", label: "Direct" },
];

const CTAS = [
  { key: "visit_website", label: "Visit website" },
  { key: "shop_now", label: "Shop now" },
  { key: "book_now", label: "Book now" },
  { key: "learn_more", label: "Learn more" },
  { key: "message_us", label: "Message us" },
  { key: "contact_us", label: "Contact us" },
  { key: "follow", label: "Follow" },
  { key: "comment", label: "Comment" },
  { key: "save_share", label: "Save / share" },
  { key: "no_cta", label: "Sometimes no CTA" },
];

const VISUAL_STYLES = [
  { key: "auto", label: "Auto", body: "Postmoro picks the best grounded style per business type." },
  { key: "grounded", label: "Grounded photography", body: "Real people, real environments, real product/service." },
  { key: "editorial", label: "Editorial", body: "Magazine-style composition with natural light." },
  { key: "product_focused", label: "Product-focused", body: "The offering is the hero of every frame." },
  { key: "lifestyle", label: "Lifestyle", body: "The offering used naturally in daily life." },
  { key: "minimal", label: "Minimal / clean", body: "Lots of negative space, few objects, restrained palette." },
  { key: "brand_led", label: "Brand-led", body: "Mirrors your own visual identity when we know it." },
];

const PROMO_BALANCES = [
  { key: "mostly_value", label: "Mostly value", body: "~20% promotional — educational, brand story, community." },
  { key: "balanced", label: "Balanced", body: "~35–40% promotional — mix of value and offers." },
  { key: "more_promotional", label: "More promotional", body: "~55–60% promotional — stronger offer frequency." },
];

// -----------------------------------------------------------------
// Small chip toggle helper — used by every multi-select field.
// -----------------------------------------------------------------
function Chip({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        "text-xs px-3 py-1.5 rounded-full border transition-all " +
        (selected
          ? "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] border-[color:var(--brand-primary)] font-semibold"
          : "bg-[color:var(--surface-2)] text-[color:var(--text-2)] border-[color:var(--border-subtle)] hover:border-[color:var(--brand-primary)]")
      }
    >
      {children}
    </button>
  );
}

function CustomListInput({ value, onChange, placeholder, testid }) {
  // Free-text CSV list, converted to array on blur.
  const [str, setStr] = useState((value || []).join(", "));
  useEffect(() => setStr((value || []).join(", ")), [JSON.stringify(value)]);
  return (
    <Input
      value={str}
      placeholder={placeholder}
      onChange={(e) => setStr(e.target.value)}
      onBlur={() => onChange(str.split(",").map((s) => s.trim()).filter(Boolean))}
      className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm"
      data-testid={testid}
    />
  );
}

// -----------------------------------------------------------------
// Sub-cards (each stays <60 lines).
// -----------------------------------------------------------------
function GoalsCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-goals-card">
      <div className="flex items-center gap-2 mb-3">
        <Target className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Goals</h3>
      </div>
      <div className="text-xs text-[color:var(--text-3)] mb-3">What do you want Autopilot to help you achieve? (multi-select)</div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="autopilot-strategy-goals-chips">
        {GOAL_OPTIONS.map((g) => (
          <Chip
            key={g.key}
            testid={`autopilot-strategy-goal-${g.key}`}
            selected={(s.strategy_goals || []).includes(g.key)}
            onClick={() => {
              const cur = new Set(s.strategy_goals || []);
              cur.has(g.key) ? cur.delete(g.key) : cur.add(g.key);
              set({ strategy_goals: [...cur] });
            }}
          >
            {g.label}
          </Chip>
        ))}
      </div>
      <Input
        value={s.strategy_custom_goal || ""}
        placeholder="Custom goal (optional)…"
        onChange={(e) => set({ strategy_custom_goal: e.target.value })}
        className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm"
        data-testid="autopilot-strategy-custom-goal"
      />
    </GlassCard>
  );
}

function ContentCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-content-card">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Content mix</h3>
      </div>
      <label className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Topics to prioritize</label>
      <div className="flex flex-wrap gap-2 mt-1 mb-3" data-testid="autopilot-strategy-priority-topics-chips">
        {TOPIC_PRIORITY.map((t) => (
          <Chip
            key={t}
            testid={`autopilot-strategy-topic-${t}`}
            selected={(s.strategy_priority_topics || []).includes(t)}
            onClick={() => {
              const cur = new Set(s.strategy_priority_topics || []);
              cur.has(t) ? cur.delete(t) : cur.add(t);
              set({ strategy_priority_topics: [...cur] });
            }}
          >
            {t.replace(/_/g, " ")}
          </Chip>
        ))}
      </div>
      <label className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Topics to AVOID (free text)</label>
      <div className="mt-1 mb-3">
        <CustomListInput
          value={s.strategy_avoid_topics}
          onChange={(arr) => set({ strategy_avoid_topics: arr })}
          placeholder="e.g. politics, generic AI news, holidays"
          testid="autopilot-strategy-avoid-topics"
        />
      </div>
      <label className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Content types</label>
      <div className="flex flex-wrap gap-2 mt-1" data-testid="autopilot-strategy-content-types-chips">
        {CONTENT_TYPES.map((t) => (
          <Chip
            key={t}
            testid={`autopilot-strategy-ctype-${t}`}
            selected={(s.strategy_content_types || []).includes(t)}
            onClick={() => {
              const cur = new Set(s.strategy_content_types || []);
              cur.has(t) ? cur.delete(t) : cur.add(t);
              set({ strategy_content_types: [...cur] });
            }}
          >
            {t.replace(/_/g, " ")}
          </Chip>
        ))}
      </div>
    </GlassCard>
  );
}

function AudienceCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-audience-card">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Audience & offerings</h3>
      </div>
      <label className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Who are you trying to reach?</label>
      <Textarea
        value={s.strategy_target_audience || ""}
        onChange={(e) => set({ strategy_target_audience: e.target.value })}
        placeholder="e.g. small business owners, local service businesses, marketing agencies"
        className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm mt-1 mb-3 min-h-[64px]"
        data-testid="autopilot-strategy-audience"
      />
      <label className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Priority products / services (comma-separated)</label>
      <div className="mt-1">
        <CustomListInput
          value={s.strategy_priority_offerings}
          onChange={(arr) => set({ strategy_priority_offerings: arr })}
          placeholder="e.g. AI content creation, Consulting, Weekly meal prep"
          testid="autopilot-strategy-priority-offerings"
        />
      </div>
    </GlassCard>
  );
}

function PromoCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-promo-card">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Promotional balance</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {PROMO_BALANCES.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => set({ strategy_promo_balance: p.key })}
            data-testid={`autopilot-strategy-promo-${p.key}`}
            className={
              "text-left rounded-lg border p-3 transition-all " +
              (s.strategy_promo_balance === p.key
                ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)]"
                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:border-[color:var(--text-3)]")
            }
          >
            <div className="text-xs font-semibold text-[color:var(--text-1)]">{p.label}</div>
            <div className="text-[11px] text-[color:var(--text-3)] mt-1">{p.body}</div>
          </button>
        ))}
      </div>
    </GlassCard>
  );
}

function VoiceCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-voice-card">
      <div className="flex items-center gap-2 mb-3">
        <Megaphone className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Brand voice</h3>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="autopilot-strategy-tone-chips">
        {TONES.map((t) => (
          <Chip
            key={t.key}
            testid={`autopilot-strategy-tone-${t.key}`}
            selected={s.strategy_tone === t.key || (!s.strategy_tone && t.key === "match_brand")}
            onClick={() => set({ strategy_tone: t.key })}
          >
            {t.label}
          </Chip>
        ))}
      </div>
    </GlassCard>
  );
}

function VisualsCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-visuals-card">
      <div className="flex items-center gap-2 mb-3">
        <Palette className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Visual style</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        {VISUAL_STYLES.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => set({ strategy_visual_style: v.key })}
            data-testid={`autopilot-strategy-visual-${v.key}`}
            className={
              "text-left rounded-lg border p-3 transition-all " +
              ((s.strategy_visual_style || "auto") === v.key
                ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)]"
                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:border-[color:var(--text-3)]")
            }
          >
            <div className="text-xs font-semibold text-[color:var(--text-1)]">{v.label}</div>
            <div className="text-[11px] text-[color:var(--text-3)] mt-1">{v.body}</div>
          </button>
        ))}
      </div>
      <label className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Visuals to avoid (free text)</label>
      <div className="mt-1">
        <CustomListInput
          value={s.strategy_visual_avoids}
          onChange={(arr) => set({ strategy_visual_avoids: arr })}
          placeholder="e.g. holograms, floating dashboards, stock photos, text-heavy graphics"
          testid="autopilot-strategy-visual-avoids"
        />
      </div>
    </GlassCard>
  );
}

function CtaCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-cta-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldOff className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Call-to-action preferences</h3>
      </div>
      <div className="text-xs text-[color:var(--text-3)] mb-3">Autopilot rotates CTAs across posts instead of repeating one.</div>
      <div className="flex flex-wrap gap-2" data-testid="autopilot-strategy-cta-chips">
        {CTAS.map((c) => (
          <Chip
            key={c.key}
            testid={`autopilot-strategy-cta-${c.key}`}
            selected={(s.strategy_cta_preferences || []).includes(c.key)}
            onClick={() => {
              const cur = new Set(s.strategy_cta_preferences || []);
              cur.has(c.key) ? cur.delete(c.key) : cur.add(c.key);
              set({ strategy_cta_preferences: [...cur] });
            }}
          >
            {c.label}
          </Chip>
        ))}
      </div>
    </GlassCard>
  );
}

function MustIncludeCard({ s, set }) {
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-mustinclude-card">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-[color:var(--brand-primary)]" />
        <h3 className="text-sm font-semibold text-[color:var(--text-1)]">Include regularly</h3>
      </div>
      <div className="text-xs text-[color:var(--text-3)] mb-2">Soft signal — Autopilot works these in over time (not every post).</div>
      <CustomListInput
        value={s.strategy_must_include}
        onChange={(arr) => set({ strategy_must_include: arr })}
        placeholder="e.g. team_content, local_references, current_offers"
        testid="autopilot-strategy-must-include"
      />
    </GlassCard>
  );
}

// -----------------------------------------------------------------
// Summary block — renders the currently-saved strategy in one glance.
// -----------------------------------------------------------------
function StrategySummary({ s }) {
  const goals = (s.strategy_goals || []).join(", ") + (s.strategy_custom_goal ? `; ${s.strategy_custom_goal}` : "");
  const rows = [
    ["Goals", goals || "—"],
    ["Priority offerings", (s.strategy_priority_offerings || []).join(", ") || "—"],
    ["Audience", s.strategy_target_audience || "—"],
    ["Prioritize", (s.strategy_priority_topics || []).join(", ") || "—"],
    ["Avoid", (s.strategy_avoid_topics || []).join(", ") || "—"],
    ["Balance", s.strategy_promo_balance || "—"],
    ["Content types", (s.strategy_content_types || []).join(", ") || "—"],
    ["Tone", s.strategy_tone || "match_brand"],
    ["CTAs", (s.strategy_cta_preferences || []).join(", ") || "—"],
    ["Visual style", s.strategy_visual_style || "auto"],
    ["Visuals to avoid", (s.strategy_visual_avoids || []).join(", ") || "—"],
    ["Include regularly", (s.strategy_must_include || []).join(", ") || "—"],
  ];
  return (
    <GlassCard className="p-4" data-testid="autopilot-strategy-summary">
      <h3 className="text-sm font-semibold text-[color:var(--text-1)] mb-3">Your Autopilot strategy</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="border-b border-[color:var(--border-subtle)] pb-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">{k}</div>
            <div className="text-[color:var(--text-1)] font-medium truncate" title={v}>{v}</div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// -----------------------------------------------------------------
// Main component
// -----------------------------------------------------------------
export default function AutopilotStrategyCard({ cfg, onCfgRefresh }) {
  const [local, setLocal] = useState(cfg || {});
  const [saving, setSaving] = useState(false);
  const dirtyRef = useMemo(() => ({ dirty: false }), []);

  useEffect(() => {
    setLocal(cfg || {});
    dirtyRef.dirty = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cfg)]);

  const set = (patch) => {
    dirtyRef.dirty = true;
    setLocal((prev) => ({ ...prev, ...patch }));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        strategy_goals: local.strategy_goals || [],
        strategy_custom_goal: local.strategy_custom_goal || "",
        strategy_priority_offerings: local.strategy_priority_offerings || [],
        strategy_target_audience: local.strategy_target_audience || "",
        strategy_priority_topics: local.strategy_priority_topics || [],
        strategy_avoid_topics: local.strategy_avoid_topics || [],
        strategy_promo_balance: local.strategy_promo_balance || "balanced",
        strategy_content_types: local.strategy_content_types || [],
        strategy_tone: local.strategy_tone || "match_brand",
        strategy_cta_preferences: local.strategy_cta_preferences || [],
        strategy_visual_style: local.strategy_visual_style || "auto",
        strategy_visual_avoids: local.strategy_visual_avoids || [],
        strategy_must_include: local.strategy_must_include || [],
        strategy_reviewed: true,
      };
      await api.put("/autopilot/settings", payload);
      toast.success("Autopilot strategy saved");
      dirtyRef.dirty = false;
      if (onCfgRefresh) await onCfgRefresh();
    } catch (_e) {
      toast.error("Could not save strategy");
    } finally {
      setSaving(false);
    }
  };

  const needsReview = !cfg?.strategy_reviewed;

  return (
    <div className="space-y-4 mb-5" data-testid="autopilot-strategy-section">
      <GlassCard className="p-5" data-testid="autopilot-strategy-hero">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-[280px] flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--brand-primary)] font-semibold">
              Autopilot Strategy
            </div>
            <div className="font-display text-xl font-semibold mt-1 text-[color:var(--text-1)]">
              Tell Postmoro what to prioritize
            </div>
            <div className="text-xs text-[color:var(--text-3)] mt-1 max-w-[560px] leading-relaxed">
              Autopilot uses these preferences on every post it generates. Change them anytime — new
              posts pick up your latest strategy. Already-scheduled posts stay as they are.
            </div>
            {needsReview ? (
              <div
                className="mt-3 text-xs px-3 py-2 rounded-md border border-[color:rgba(255,190,110,0.55)] bg-[color:rgba(255,190,110,0.10)] text-[color:var(--accent-warm)] inline-block"
                data-testid="autopilot-strategy-review-nudge"
              >
                Review your Autopilot strategy — or click Save to accept the recommended defaults.
              </div>
            ) : null}
          </div>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold min-w-[140px]"
            data-testid="autopilot-strategy-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {saving ? "Saving…" : "Save strategy"}
          </Button>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <GoalsCard s={local} set={set} />
        <AudienceCard s={local} set={set} />
        <ContentCard s={local} set={set} />
        <PromoCard s={local} set={set} />
        <VoiceCard s={local} set={set} />
        <VisualsCard s={local} set={set} />
        <CtaCard s={local} set={set} />
        <MustIncludeCard s={local} set={set} />
      </div>

      <StrategySummary s={local} />
    </div>
  );
}
